import React from 'react';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';

import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { AlertCircle, CalendarClock, CheckCircle, Edit, Filter, Receipt, Trash2, Wallet, ChevronDown, Calendar } from 'lucide-react';
import { cn } from '../../lib/utils';
import { formatDate, formatMoney } from './constants';

export default function AcomptesTab({
  acomptesStats,
  expandedIds,
  filterAcompteStatus,
  generateAcompteRecuReference,
  getAllAcomptesFlat,
  handlePrintRecuAcompte,
  openDeleteAcompteModal,
  openEditAcompteModal,
  setFilterAcompteStatus,
  toggleExpand
}) {
  return (
    <>
      {/* Stats compactes */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
        <div className="flex items-center gap-3 p-3 rounded-lg border bg-white">
          <Wallet className="h-4 w-4 text-amber-500 shrink-0" />
          <div>
            <p className="text-[10px] text-gray-400 uppercase tracking-wider">Total</p>
            <p className="text-lg font-bold text-gray-900">{acomptesStats.total}</p>
          </div>
        </div>
        <div className="flex items-center gap-3 p-3 rounded-lg border bg-white">
          <CheckCircle className="h-4 w-4 text-emerald-500 shrink-0" />
          <div>
            <p className="text-[10px] text-gray-400 uppercase tracking-wider">Recus</p>
            <p className="text-lg font-bold text-emerald-700">{acomptesStats.recus}</p>
            <p className="text-[10px] text-gray-400">{formatMoney(acomptesStats.montantRecu)} EUR</p>
          </div>
        </div>
        <div className="flex items-center gap-3 p-3 rounded-lg border bg-white">
          <CalendarClock className="h-4 w-4 text-blue-500 shrink-0" />
          <div>
            <p className="text-[10px] text-gray-400 uppercase tracking-wider">Programmes</p>
            <p className="text-lg font-bold text-blue-700">{acomptesStats.programmes}</p>
            <p className="text-[10px] text-gray-400">{formatMoney(acomptesStats.montantProgramme)} EUR</p>
          </div>
        </div>
        <div className="flex items-center gap-3 p-3 rounded-lg border bg-white">
          <Trash2 className="h-4 w-4 text-rose-500 shrink-0" />
          <div>
            <p className="text-[10px] text-gray-400 uppercase tracking-wider">Annules</p>
            <p className="text-lg font-bold text-rose-700">{acomptesStats.annules}</p>
          </div>
        </div>
      </div>

      {/* Filtre */}
      <div className="flex flex-wrap items-center gap-3">
        <Filter className="h-4 w-4 text-amber-600" />
        <Select value={filterAcompteStatus} onValueChange={setFilterAcompteStatus}>
          <SelectTrigger className="w-52">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Tous les acomptes</SelectItem>
            <SelectItem value="recu">Recus uniquement</SelectItem>
            <SelectItem value="programme">Programmes uniquement</SelectItem>
            <SelectItem value="annule">Annules uniquement</SelectItem>
          </SelectContent>
        </Select>
        <span className="text-sm text-gray-500">{getAllAcomptesFlat.length} acompte(s)</span>
      </div>

      {/* Liste */}
      {getAllAcomptesFlat.length === 0 ? (
        <div className="bg-white rounded-xl border p-12 text-center">
          <Wallet className="h-12 w-12 mx-auto text-gray-300 mb-3" />
          <p className="text-gray-700 font-medium">Aucun acompte enregistre</p>
          <p className="text-sm text-gray-400">Les acomptes apparaitront ici une fois enregistres</p>
        </div>
      ) : (
        <div className="space-y-1" data-testid="acomptes-list">
          {getAllAcomptesFlat.map((acompte, idx) => {
            const acId = `ac-${acompte.docReference}-${idx}`;
            const isExpanded = expandedIds.has(acId);
            const isScheduled = acompte.is_scheduled || acompte.statut === 'programme';
            const isCancelled = acompte.statut === 'annule';
            const refLabel = generateAcompteRecuReference(acompte.doc, acompte.acompteIndex);

            return (
              <div
                key={acId}
                className={cn(
                  "rounded-lg border transition-all duration-150",
                  isCancelled ? "opacity-60" : "",
                  isExpanded ? "border-amber-200 shadow-sm bg-white" : "border-gray-200 bg-white hover:border-gray-300 hover:shadow-sm"
                )}
                data-testid={`acompte-row-${idx}`}
              >
                {/* Compact row */}
                <button onClick={() => toggleExpand(acId)} className="flex items-center gap-3 px-4 py-2.5 w-full text-left" data-testid={`acompte-toggle-${idx}`}>
                  <Wallet className={cn("h-4 w-4 shrink-0", isCancelled ? "text-gray-400" : isScheduled ? "text-blue-500" : "text-amber-500")} />
                  <div className="min-w-0 flex-1">
                    <p className={cn("font-medium text-sm truncate", isExpanded ? "text-amber-700" : "text-gray-900")}>{acompte.docName}</p>
                    <p className="text-[10px] text-gray-400 font-mono truncate">{refLabel}</p>
                  </div>
                  {isCancelled ? (
                    <Badge className="bg-rose-700 text-white text-[10px] shrink-0 font-semibold"><AlertCircle className="h-3 w-3 mr-0.5" />Annule</Badge>
                  ) : isScheduled ? (
                    <Badge className="bg-blue-700 text-white text-[10px] shrink-0 font-semibold"><CalendarClock className="h-3 w-3 mr-0.5" />Programme</Badge>
                  ) : (
                    <Badge className="bg-emerald-700 text-white text-[10px] shrink-0 font-semibold"><CheckCircle className="h-3 w-3 mr-0.5" />Recu</Badge>
                  )}
                  <div className="hidden md:flex items-center gap-1 text-xs text-gray-500 shrink-0">
                    <Calendar className="h-3 w-3" /><span>{formatDate(acompte.date)}</span>
                  </div>
                  <span className="text-sm font-bold text-amber-700 tabular-nums shrink-0">{formatMoney(acompte.montant)} EUR</span>
                  <ChevronDown className={cn("h-4 w-4 text-gray-400 shrink-0 transition-transform duration-200", isExpanded && "rotate-180 text-amber-500")} />
                </button>

                {/* Expanded */}
                {isExpanded && (
                  <div className="px-4 pb-4 pt-1 border-t border-gray-100 animate-in fade-in slide-in-from-top-1 duration-150" data-testid={`acompte-details-${idx}`}>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      <div className="space-y-2">
                        <h4 className="text-[11px] font-semibold text-gray-400 uppercase tracking-wider">Souscripteur</h4>
                        <div className="space-y-1.5 text-sm">
                          <p className="font-medium text-gray-900">{acompte.docName}</p>
                          <p className="text-gray-500 text-xs font-mono">{acompte.docReference}</p>
                        </div>
                      </div>
                      <div className="space-y-2">
                        <h4 className="text-[11px] font-semibold text-gray-400 uppercase tracking-wider">Details</h4>
                        <div className="space-y-1.5 text-sm">
                          <div className="flex justify-between"><span className="text-gray-500">Reference:</span><span className="font-mono text-xs">{refLabel}</span></div>
                          <div className="flex justify-between"><span className="text-gray-500">Montant:</span><span className="font-bold text-amber-700">{formatMoney(acompte.montant)} EUR</span></div>
                          <div className="flex justify-between"><span className="text-gray-500">Date:</span><span className="text-gray-700">{formatDate(acompte.date)}</span></div>
                          <div className="flex justify-between"><span className="text-gray-500">Mode:</span><Badge variant="outline" className="text-[10px] capitalize">{acompte.mode?.replace('_', ' ') || 'Virement'}</Badge></div>
                        </div>
                      </div>
                      <div className="space-y-2">
                        <h4 className="text-[11px] font-semibold text-gray-400 uppercase tracking-wider">Actions</h4>
                        <div className="flex flex-wrap gap-1.5">
                          <Button variant="outline" size="sm" className="h-8 text-xs gap-1.5" data-testid={`acompte-recu-${idx}`}
                            onClick={(e) => { e.stopPropagation(); handlePrintRecuAcompte(acompte.doc, acompte, acompte.acompteIndex); }}
                          ><Receipt className="h-3.5 w-3.5" /> Recu</Button>
                          {!isCancelled && (
                            <Button variant="outline" size="sm" className="h-8 text-xs gap-1.5 border-blue-200 text-blue-700 hover:bg-blue-50" data-testid={`acompte-edit-${idx}`}
                              onClick={(e) => { e.stopPropagation(); openEditAcompteModal(acompte.doc, acompte, acompte.acompteIndex); }}
                            ><Edit className="h-3.5 w-3.5" /> Modifier</Button>
                          )}
                          {!isCancelled && (
                            <Button variant="outline" size="sm" className="h-8 text-xs gap-1.5 border-red-200 text-red-700 hover:bg-red-50" data-testid={`acompte-delete-${idx}`}
                              onClick={(e) => { e.stopPropagation(); openDeleteAcompteModal(acompte.doc, acompte, acompte.acompteIndex); }}
                            ><Trash2 className="h-3.5 w-3.5" /> Annuler</Button>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* Synthese */}
      {getAllAcomptesFlat.length > 0 && (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-2 mt-2">
          <div className="p-3 bg-amber-50 rounded-lg border border-amber-200 text-center">
            <p className="text-[10px] text-amber-600 uppercase font-semibold mb-1">Total affiche</p>
            <p className="text-xl font-bold text-amber-700">{formatMoney(getAllAcomptesFlat.filter(a => a.statut !== 'annule').reduce((s, a) => s + (a.montant || 0), 0))} EUR</p>
          </div>
          <div className="p-3 bg-emerald-50 rounded-lg border border-emerald-200 text-center">
            <p className="text-[10px] text-emerald-600 uppercase font-semibold mb-1">Recus</p>
            <p className="text-xl font-bold text-emerald-700">{formatMoney(getAllAcomptesFlat.filter(a => !a.is_scheduled && a.statut !== 'programme' && a.statut !== 'annule').reduce((s, a) => s + (a.montant || 0), 0))} EUR</p>
          </div>
          <div className="p-3 bg-blue-50 rounded-lg border border-blue-200 text-center">
            <p className="text-[10px] text-blue-600 uppercase font-semibold mb-1">Programmes</p>
            <p className="text-xl font-bold text-blue-700">{formatMoney(getAllAcomptesFlat.filter(a => (a.is_scheduled || a.statut === 'programme') && a.statut !== 'annule').reduce((s, a) => s + (a.montant || 0), 0))} EUR</p>
          </div>
        </div>
      )}
    </>
  );
}
